import { PortableText } from '@portabletext/react'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { IS_DEVELOPMENT, RINGLE_DOMAIN } from '../../modules/envVars'
import useAppRouter from '../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../modules/hooks/common/useIsWebview'
import client from '../../modules/sanityClient'
import urls from '../../modules/urls'
import { buildSanityImage } from '../../modules/utils'
import { INTERNAL_LINK_QUERY } from '../../sanity/groqQueries'
import { INTERNAL_LINK_QUERYResult } from '../../sanity/types'

export function updateMarksToBreak(data) {
  for (let i = 0; i < data?.length; i++) {
    const block = data[i]
    if (
      block._type === 'block' &&
      block.children &&
      block.children.length > 0 &&
      block.children[0]._type === 'span' &&
      block.children[0]?.marks?.length === 0 &&
      block.children[0].text === ''
    ) {
      block.children[0].marks = ['breakLine']
    }
  }
  return data
}

export function convertToEmbedUrl(url) {
  // 'youtube.com' 또는 'youtu.be' 도메인이 있는지 확인합니다.
  if (url?.includes('youtube.com') || url?.includes('youtu.be')) {
    // 'youtube.com' 도메인에서 비디오 ID를 추출합니다.
    if (url.includes('youtube.com')) {
      const videoId = url.split('v=')[1]
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`
      }
    }
    // 'youtu.be' 도메인에서 비디오 ID를 추출합니다.
    else if (url.includes('youtu.be')) {
      const videoId = url.split('.be/')[1]
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`
      }
    }
  }

  // 변환할 수 없는 URL이면 원래 URL을 반환합니다.
  return url
}

export const InternalLink = (props) => {
  const { value } = props
  const [slug, setSlug] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = {
          refId: value.reference._ref,
        }

        const data: INTERNAL_LINK_QUERYResult = await client.fetch(INTERNAL_LINK_QUERY, params)

        if (data) {
          setSlug(data[0]?.slug || null)
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [value?.reference?._ref])

  if (!slug) {
    return null // 로딩 중 또는 데이터가 없을 때에 대한 처리
  }
  return (
    <span className="text-blue-500 underline">
      <a
        href={`https://${IS_DEVELOPMENT ? 'qa7.ringlefront.com' : RINGLE_DOMAIN}/ko${urls.student.landing.blog.detail(
          slug
        )}`}
      >
        {props.children}
      </a>
    </span>
  )
}

const getExpressionMarks = () => {
  const marks = {}

  for (let i = 1; i <= 12; i++) {
    marks[`E${i}`] = (props) => {
      return (
        <a href={`#E${i}`} className="text-purple-400 underline print:font-black print:text-black print:no-underline">
          {props.children}
        </a>
      )
    }
  }

  return marks
}

export const expressionMarks = getExpressionMarks()

export const h2 = (props) => {
  return <h2 className="Headline2-24Bd">{props.children}</h2>
}
export const h3 = (props) => {
  return <h3 className="Headline3-22Bd">{props.children}</h3>
}
export const h4 = (props) => {
  return <h4 className="Title1-20Bd">{props.children}</h4>
}
export const h5 = (props) => {
  return <h5 className="Title2-18Bd">{props.children}</h5>
}
export const h6 = (props) => {
  return <h6 className="Body1-16Md">{props.children}</h6>
}
export const bullet = (props) => {
  return <li className="">{props.children}</li>
}

export const insightExpression = (props) => {
  return (
    <a style={{ color: '#9B8AEE' }} className="Title1-20Bd" href={`#${props.children}`}>
      {props.children}
    </a>
  )
}

export const color = (props) => {
  return <span style={{ color: props.value.hex }}>{props.children}</span>
}
export const Link = (props) => {
  const isWebview = useIsWebview()
  const appRouter = useAppRouter()
  return (
    <span className="text-blue-500 underline">
      <button
        onClick={() => (isWebview ? appRouter.linking(props.value.href) : window.open(props.value.href, '_blank'))}
      >
        {props.children}
      </button>
    </span>
  )
}
export const mailto = (props) => {
  return (
    <span className="text-blue-500 underline">
      <a href={props.value.href} target="_blank" rel="noreferrer">
        {props.children}
      </a>
    </span>
  )
}
export const purple = (props) => {
  return <span className="text-purple-500">{props.children}</span>
}
export const purple400 = (props) => {
  return <span className="text-purple-400">{props.children}</span>
}
export const yellow = (props) => {
  return <span className="text-yellow-500">{props.children}</span>
}
export const green = (props) => {
  return <span className="text-green-500">{props.children}</span>
}
export const gray = (props) => {
  return <span className="text-gray-500">{props.children}</span>
}
export const red = (props) => {
  return <span className="text-red-500">{props.children}</span>
}
export const blue = (props) => {
  return <span className="text-blue-500">{props.children}</span>
}
export const blueHigh = (props) => {
  return <span className="bg-blue-100">{props.children}</span>
}
export const redHigh = (props) => {
  return <span className="bg-red-100">{props.children}</span>
}
export const greenHigh = (props) => {
  return <span className="bg-green-100">{props.children}</span>
}
export const purpleHigh = (props) => {
  return <span className="bg-purple-100">{props.children}</span>
}
export const yellowHigh = (props) => {
  return <span className="bg-yellow-100">{props.children}</span>
}
export const span = (props) => {
  return <span>{props.children}</span>
}
export const breakLine = (props) => {
  return <br />
}
export const right = (props) => {
  return <div className="flex w-full justify-end">{props.children}</div>
}
export const left = (props) => {
  return <div className="justify-left flex w-full">{props.children}</div>
}
export const center = (props) => {
  return <div className="flex w-full justify-center">{props.children}</div>
}
export const strong = (props) => {
  return <span className="font-bold">{props.children}</span>
}

export const image = ({ value }: any) => {
  if (!value?.asset?._ref) {
    return null
  }
  const imageUrl = (width, height) => {
    if (width && height) {
      return buildSanityImage(value).width(width).height(height).url()
    } else if (width && !height) {
      return buildSanityImage(value).width(width).url()
    } else if (!width && height) {
      return buildSanityImage(value).height(height).url()
    } else {
      return buildSanityImage(value).fit('max').url()
    }
  }
  return (
    <>
      {value.width && value.height ? (
        <Image
          alt={value.alt || ' '}
          loading="lazy"
          src={imageUrl(value.width, value.height) as any}
          width={value.width}
          height={value.height}
          style={{ borderRadius: `${value.imageRadius || 0}px` }}
          className="mx-auto"
        />
      ) : (
        <Image
          className="max-w-auto mx-auto w-full"
          alt={value.alt || ' '}
          loading="lazy"
          src={buildSanityImage(value).fit('max').url()}
          width={800}
          height={800}
          style={{ borderRadius: `${value.imageRadius || 0}px` }}
        />
      )}
    </>
  )
}
export const blockQuote = ({ value }: any) => {
  const content = updateMarksToBreak(value.content || [])

  return (
    <div className="flex w-full flex-col rounded-[8px] bg-gray-100 p-[20px]">
      <div className="Title2-18Bd mb-[32px]">{value.title}</div>
      <div className="Body1-16Md">
        <PortableText value={content} components={ptComponents} />
      </div>
    </div>
  )
}
export const breakLine2 = ({ value }: any) => {
  return <hr className="my-[20px]" />
}
export const youtube = ({ value }: any) => {
  return (
    <div className="aspect-[16/9]">
      <iframe
        width="100%"
        height="100%"
        className="aspect-[16/9]"
        src={convertToEmbedUrl(value.link)}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      ></iframe>
    </div>
  )
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export const block = {
  h2: h2,
  h3: h3,
  h4: h4,
  h5: h5,
  h6: h6,
  bullet: bullet,
}
export const marks = {
  insightExpression: insightExpression,
  color: color,
  link: Link,
  mailto: mailto,
  internalLink: InternalLink,
  purple: purple,
  purple400: purple400,
  yellow: yellow,
  green: green,
  gray: gray,
  red: red,
  blue: blue,
  blueHigh: blueHigh,
  redHigh: redHigh,
  greenHigh: greenHigh,
  purpleHigh: purpleHigh,
  yellowHigh: yellowHigh,
  span: span,
  breakLine: breakLine,
  right: right,
  left: left,
  center: center,
  strong: strong,
}
export const types = {
  image: image,
  blockQuote: blockQuote,
  breakLine: breakLine2,
  youtube: youtube,
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export const ptComponents = {
  block: block,
  marks: { ...expressionMarks, ...marks },
  types: types,
}
